import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Small persistent banner shown at the top of authenticated pages when a
 * user still needs to (a) confirm their email or (b) pick a username. The
 * banner is dismissible per-session (`sessionStorage`) so it doesn't nag on
 * every page load — but the underlying gate stays: content-creation
 * endpoints still 403 until both conditions are cleared.
 */
export default function AccountSetupBanner() {
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("willoah.account_setup.dismissed.v1") === "1";
  });

  if (!user) return null;
  // Feb 23 (Kelly): dropped the "pick a username" nag — identity now comes
  // entirely from the existing Willoah account (email + Roots profile
  // name). Only the email-verification variant remains, and only for
  // password-auth users who haven't clicked their link yet.
  const needsVerification = user.auth_method === "password" && !user.email_verified;
  if (!needsVerification) return null;
  if (dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Sent a fresh link. Check your inbox.");
      await refresh();
    } catch {
      toast.error("Couldn't send the link right now. Try again in a minute.");
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("willoah.account_setup.dismissed.v1", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="setup-banner-verification"
      className="border-b border-[#B79256]/30 bg-[#B79256]/10"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <Mail className="w-4 h-4 text-[#4E5A3D] shrink-0" strokeWidth={1.75} />
        <div className="flex-1 min-w-0 text-[#2A2823]/85">
          Confirm your email to plant seeds. We sent a link to{" "}
          <span className="font-medium text-[#2A2823]">{user.email}</span>.
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          data-testid="setup-banner-resend"
          className="text-[#4E5A3D] underline underline-offset-2 hover:text-[#3E4830] disabled:opacity-60"
        >
          {sending ? "Sending…" : "Resend link"}
        </button>
        <button
          onClick={dismiss}
          data-testid="setup-banner-dismiss"
          aria-label="Dismiss"
          className="p-1 text-[#2A2823]/40 hover:text-[#2A2823]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
